import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './ReadMoreEvent.module.css';

interface EventItem {
  id: number;
  title: string;
  image: string;
  image_url: string;
  description?: string;
  date?: string;
  author?: string;
  comments?: number;
}

interface LocationState {
  eventData: EventItem;
}

const ReadMoreEvent: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState<EventItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Get event data from navigation state or fetch from API
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        // Check if data was passed via navigation state
        const eventDataFromState = (location.state as LocationState)?.eventData;
        
        if (eventDataFromState) {
          setEvent(eventDataFromState);
          setLoading(false);
          return;
        }
        
        // If no state data, fetch from API
        if (!eventId) {
          throw new Error('No event ID provided');
        }
        
        console.log(`Fetching event ${eventId} from API...`);
        const response = await fetch(apiUrl(`/api/events/${eventId}`));
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setEvent(result.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        setError(`Failed to load event: ${err instanceof Error ? err.message : 'Unknown error'}`);
        console.error('Error fetching event:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId, location.state]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading event details...</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={styles.container}>
        <div className={styles.notFound}>
          <h2>Event Not Found</h2>
          <p>{error || `The requested event could not be found.`}</p>
          <button onClick={() => navigate('/')} className={styles.backBtn}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.readMoreContainer}>
      <div className={styles.container}>
        {/* Back Button */}
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <i className="fas fa-arrow-left"></i>
          Back to Events
        </button>

        {/* Event Header */}
        <div className={styles.eventHeader}>
          <h1 className={styles.eventTitle}>{event.title}</h1>
          <div className={styles.eventMeta}>
            {event.date && (
              <span className={styles.eventDate}>
                <i className="fas fa-calendar"></i>
                {event.date}
              </span>
            )}
            {event.author && (
              <span className={styles.eventAuthor}>
                <i className="fas fa-user"></i>
                {event.author}
              </span>
            )}
            {event.comments !== undefined && (
              <span className={styles.eventComments}>
                <i className="fas fa-comment"></i>
                Comments ({event.comments})
              </span>
            )}
          </div>
        </div>

        {/* Event Image */}
        <div className={styles.eventImage}>
          <img 
            src={event.image_url} 
            alt={event.title}
            className={styles.mainImage}
            onError={(e) => {
              const firstLetter = event.title?.charAt(0) || 'E';
              (e.target as HTMLImageElement).src = `https://placehold.co/1200x600/2563eb/white?text=${firstLetter}`;
            }}
          />
        </div>

        {/* Event Description */}
        <div className={styles.eventDescription}>
          <h2>About This Event</h2>
          <p>{event.description || "No description available for this event."}</p>
          
          <div className={styles.additionalInfo}>
            <h3>Event Details</h3>
            <p>
              This event represents NORA's ongoing commitment to creating positive change in Afghan society. 
              Through initiatives like "{event.title}", we strive to empower communities and build a brighter future 
              for all Afghans. Our dedicated team works tirelessly to ensure each event makes a meaningful impact 
              and contributes to sustainable development.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadMoreEvent;